import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPost } from '../../services/posts';
import { getErrorMessage } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../lib/initials';
import { resizeImageFile } from '../../lib/image';

interface PostComposerProps {
  eventId?: string;
  onPostCreated?: () => void;
}

export function PostComposer({ eventId, onPostCreated }: PostComposerProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [expiresMonths, setExpiresMonths] = useState<number>(3);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const createPostMutation = useMutation({
    mutationFn: createPost,
    onSuccess: () => {
      // Invalidate feed to refetch
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setTitle('');
      setContent('');
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
      setSelectedImages([]);
      setImagePreviews([]);
      setSelectedPdf(null);
      setError('');
      if (onPostCreated) onPostCreated();
    },
    onError: (err) => {
      setError(getErrorMessage(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!eventId && !title.trim()) {
      setError('Overskrift er påkrævet for opslag uden begivenhed');
      return;
    }
    
    if (!content.trim() && selectedImages.length === 0 && !selectedPdf) {
      setError('Opslag skal indeholde tekst, et billede eller en PDF');
      return;
    }

    createPostMutation.mutate({ 
      title: title.trim() || undefined,
      content: content.trim(),
      images: selectedImages,
      pdf: selectedPdf ?? undefined,
      eventId,
      expiresMonths: eventId ? undefined : expiresMonths,
    });
  };

  const handleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    const totalAfterSelection = selectedImages.length + files.length;
    if (totalAfterSelection > 10) {
      setError('Du kan højst uploade 10 billeder pr. opslag');
      return;
    }

    try {
      const resizedFiles = await Promise.all(
        files.map(async (file) => {
          if (!file.type.startsWith('image/')) {
            throw new Error('Kun billedfiler er tilladt');
          }

          if (file.size > 10 * 1024 * 1024) {
            throw new Error('Et billede må højst være 10MB før komprimering');
          }

          return resizeImageFile(file, 1600, 0.82);
        })
      );

      const previews = resizedFiles.map((file) => URL.createObjectURL(file));
      setSelectedImages((prev) => [...prev, ...resizedFiles]);
      setImagePreviews((prev) => [...prev, ...previews]);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke behandle billeder');
    } finally {
      event.target.value = '';
    }
  };

  const removeImageAt = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const previewToRemove = prev[index];
      if (previewToRemove) {
        URL.revokeObjectURL(previewToRemove);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <form onSubmit={handleSubmit}>
        {!eventId && (
          <div className="mb-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Overskrift"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              maxLength={120}
            />
          </div>
        )}

        <div className="flex gap-3">
          {/* Avatar */}
          <div className="flex-shrink-0">
            {user?.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-bold text-sm">{getInitials(user?.name || '')}</span>
              </div>
            )}
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={eventId ? 'Skriv en kommentar...' : 'Hvad tænker du på?'}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            rows={3}
            maxLength={5000}
          />
        </div>

        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700 transition-colors">
              Tilføj billeder
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelection}
                className="hidden"
              />
            </label>

            <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-700 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {selectedPdf ? selectedPdf.name : 'Tilføj PDF'}
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 20 * 1024 * 1024) {
                      setError('PDF må højst være 20MB');
                    } else {
                      setSelectedPdf(file);
                      setError('');
                    }
                  }
                  e.target.value = '';
                }}
                className="hidden"
              />
            </label>

            {selectedPdf && (
              <button
                type="button"
                onClick={() => setSelectedPdf(null)}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                Fjern PDF
              </button>
            )}
          </div>

          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {imagePreviews.map((preview, index) => (
                <div key={preview} className="relative">
                  <img
                    src={preview}
                    alt={`Valgt billede ${index + 1}`}
                    className="w-full h-28 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeImageAt(index)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 text-xs hover:bg-black/80"
                    aria-label={`Fjern billede ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500">
              {!eventId ? `${title.length} / 120 • ` : ''}{content.length} / 5000
            </div>
            {!eventId && (
              <div className="flex items-center gap-1.5">
                <label htmlFor="expiresMonths" className="text-xs text-gray-500 whitespace-nowrap">
                  Udløber om:
                </label>
                <select
                  id="expiresMonths"
                  value={expiresMonths}
                  onChange={(e) => setExpiresMonths(Number(e.target.value))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value={1}>1 måned</option>
                  <option value={3}>3 måneder</option>
                  <option value={6}>6 måneder</option>
                  <option value={12}>1 år</option>
                </select>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={createPostMutation.isPending || (!content.trim() && selectedImages.length === 0 && !selectedPdf) || (!eventId && !title.trim())}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createPostMutation.isPending ? 'Sender...' : (eventId ? 'Send' : 'Del opslag')}
          </button>
        </div>
      </form>
    </div>
  );
}
